import React, { memo, useCallback } from 'react';
import classNames from 'classnames';
import { useWtrSelector } from 'redux/hooks';
import { colourScallopGrey } from '@johnlewispartnership/wtr-ingredients/foundations/colours';
import { Success } from '@johnlewispartnership/wtr-ingredients/foundations/icons';
import {
  getBundleTitle,
  getBundleProductsGrouped,
  getBundleLoadingStatus,
  getBundleBuilderItems,
  getBundleNotFound,
  getBundleHasError,
} from 'redux/modules/bundle/selectors';
import { getPageLoading } from 'redux/modules/page/selectors';
import MetaHeader from 'components/MetaHeader';
import Breadcrumbs from 'components/Breadcrumbs';
import ContentLocation from 'components/ContentLocation';
import ProductGrid from 'components/ProductGrid/ProductGrid';
import { SSRMediaQuery } from 'components/SSRMediaQuery';
import BundleLineItemPlaceholder from 'components/BundleBuilderPage/BundleLineItemPlaceHolder';
import Builder from 'components/Builder';
import NotFound from 'components/NotFound/NotFound';
import Skeleton from 'components/Skeleton';
import { BREAKPOINTS } from 'constants/grid';
import { SEARCH_TYPE } from './constants';

import styles from './BundleBuilderPage.scss';

const taxonomyLevel = 'F0';

const location = 'bundle';

const defaultIcon = <Success size="large" className={styles.icon} />;

const EMPTY_PRODUCTS: never[] = [];

const BundleBuilderPage = memo(() => {
  const groups = useWtrSelector(getBundleProductsGrouped);
  const title = useWtrSelector(getBundleTitle);
  const isPageLoading = useWtrSelector(getPageLoading);
  const isBundleLoading = useWtrSelector(getBundleLoadingStatus);
  const notFound = useWtrSelector(getBundleNotFound);
  const hasError = useWtrSelector(getBundleHasError);
  const builder = useWtrSelector(getBundleBuilderItems);

  const getProducts = useCallback(
    (groupId: string) => groups.get(groupId) || EMPTY_PRODUCTS,
    [groups],
  );

  if (hasError) {
    return null;
  }

  if (notFound) {
    return <NotFound />;
  }

  const pageLoading = isPageLoading || isBundleLoading;

  return (
    <div data-testid="bundle-page" className={styles.wrapper}>
      <MetaHeader title={title} notIndexed />
      <Breadcrumbs breadcrumbs={[]} />
      <ContentLocation name="header" preloadImages />
      <section className={classNames(styles.bundlesContainer, 'container-fluid')}>
        {pageLoading ? (
          <Skeleton
            withSpinner
            spinnerPosition="top"
            height="100vh"
            backgroundColor={colourScallopGrey}
            data-testid="products-loading"
          />
        ) : (
          <>
            <SSRMediaQuery minWidth={BREAKPOINTS.lg}>
              <div
                className={classNames('col-sm-12of12', 'col-lg-8of12', styles.productsContainer)}
              >
                <ProductGrid
                  isBuilderPage
                  groups={groups}
                  products={[...groups.values()].flat()}
                  loading={isBundleLoading}
                  resetError={() => {}}
                  searchType={SEARCH_TYPE}
                  taxonomyLevel={taxonomyLevel}
                />
              </div>
            </SSRMediaQuery>
            <Builder
              alwaysOpen
              builders={builder}
              className={styles.offersBuilder}
              GroupItemPlaceholder={BundleLineItemPlaceholder}
              location={location}
              completeMessage="Recommended amount reached. Feel free to add more!"
              initialMessage={`Create your ${title}`}
              defaultIcon={defaultIcon}
              getProducts={getProducts}
              searchType={SEARCH_TYPE}
              taxonomyLevel={taxonomyLevel}
            />
          </>
        )}
      </section>
    </div>
  );
});

BundleBuilderPage.displayName = 'BundleBuilderPage';

export default BundleBuilderPage;
